import * as THREE from "three";

export type ClipAxis = "x" | "y" | "z";

/** Per-axis state for one clipping plane. */
interface AxisPlane {
  plane: THREE.Plane;
  /** Group containing the frame line + faint fill quad. */
  helper: THREE.Group;
  /** Faint fill mesh — also used as raycast target for drag. */
  fill: THREE.Mesh;
  enabled: boolean;
  offset: number;
}

const AXIS_COLORS: Record<ClipAxis, number> = { x: 0xff4444, y: 0x44ff44, z: 0x4488ff };
const PLANE_SIZE_FACTOR = 1.4;
const FILL_OPACITY = 0.04;
const FRAME_OPACITY = 0.6;

/**
 * Manages up to 3 clipping planes (one per axis).
 * Visual: thin colored frame + very faint fill (option B).
 */
export class ClipPlanes {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private container: HTMLElement;
  private modelSize = new THREE.Vector3(1, 1, 1);
  private modelCenter = new THREE.Vector3();

  private gizmoGroup = new THREE.Group();
  private handles: Record<ClipAxis, THREE.Mesh>;
  private axes: Record<ClipAxis, AxisPlane>;

  private active = false;
  private dragging: ClipAxis | null = null;
  private dragStartY = 0;
  private dragStartOffset = 0;
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();

  onChange: (() => void) | null = null;

  constructor(
    _renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    container: HTMLElement,
  ) {
    this.scene = scene;
    this.camera = camera;
    this.container = container;

    this.axes = {
      x: this._createAxisPlane("x"),
      y: this._createAxisPlane("y"),
      z: this._createAxisPlane("z"),
    };

    this.handles = {
      x: this._createHandle("x"),
      y: this._createHandle("y"),
      z: this._createHandle("z"),
    };

    this.gizmoGroup.visible = false;
    this.scene.add(this.gizmoGroup);

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  setModelBounds(size: THREE.Vector3, center: THREE.Vector3): void {
    this.modelSize = size.clone();
    this.modelCenter = center.clone();

    this.gizmoGroup.position.copy(center);

    const maxDim = Math.max(size.x, size.y, size.z);
    const handleLen = maxDim * 0.35;
    const handleRad = maxDim * 0.012;
    for (const axis of ["x", "y", "z"] as ClipAxis[]) {
      const h = this.handles[axis];
      h.scale.set(handleRad, handleLen, handleRad);
      const pos = new THREE.Vector3();
      pos[axis] = handleLen * 0.5 + maxDim * 0.02;
      h.position.copy(pos);
    }

    // Rebuild helper geometry to match new model size
    for (const axis of ["x", "y", "z"] as ClipAxis[]) {
      const ap = this.axes[axis];
      this._rebuildHelper(axis, ap);
      ap.offset = 0;
      this._updatePlanePosition(axis);
    }
  }

  setActive(on: boolean): void {
    if (on === this.active) return;
    this.active = on;
    this.gizmoGroup.visible = on;

    // Show/hide helpers for already-enabled axes
    for (const axis of ["x", "y", "z"] as ClipAxis[]) {
      if (this.axes[axis].enabled) {
        this.axes[axis].helper.visible = on;
      }
    }

    if (on) {
      this.container.addEventListener("pointerdown", this._onPointerDown);
      this.container.addEventListener("pointermove", this._onPointerMove);
      this.container.addEventListener("pointerup", this._onPointerUp);
    } else {
      this.container.removeEventListener("pointerdown", this._onPointerDown);
      this.container.removeEventListener("pointermove", this._onPointerMove);
      this.container.removeEventListener("pointerup", this._onPointerUp);
      this.dragging = null;
    }
  }

  get isActive(): boolean {
    return this.active;
  }

  toggleAxis(axis: ClipAxis): void {
    const ap = this.axes[axis];
    ap.enabled = !ap.enabled;
    ap.helper.visible = ap.enabled && this.active;
    this.onChange?.();
  }

  setAxisOffset(axis: ClipAxis, offset: number): number {
    const ap = this.axes[axis];
    const halfSize = this.modelSize[axis] * 0.5;
    const clamped = Math.max(-halfSize, Math.min(halfSize, offset));
    ap.offset = clamped;
    this._updatePlanePosition(axis);
    this.onChange?.();
    return clamped;
  }

  /** Enable an axis silently (no helper visual). Used for plan mode auto-clip. */
  enableAxis(axis: ClipAxis, offset: number): void {
    const ap = this.axes[axis];
    ap.enabled = true;
    // Never show helper for programmatic enables (plan mode)
    ap.helper.visible = false;
    this.setAxisOffset(axis, offset);
  }

  disableAxis(axis: ClipAxis): void {
    const ap = this.axes[axis];
    ap.enabled = false;
    ap.helper.visible = false;
    ap.offset = 0;
    this._updatePlanePosition(axis);
    this.onChange?.();
  }

  getActivePlanes(): THREE.Plane[] {
    const planes: THREE.Plane[] = [];
    for (const axis of ["x", "y", "z"] as ClipAxis[]) {
      if (this.axes[axis].enabled) {
        planes.push(this.axes[axis].plane);
      }
    }
    return planes;
  }

  getAxisState(axis: ClipAxis): { enabled: boolean; offset: number } {
    return { enabled: this.axes[axis].enabled, offset: this.axes[axis].offset };
  }

  getModelSize(): THREE.Vector3 {
    return this.modelSize;
  }

  dispose(): void {
    this.setActive(false);
    this.gizmoGroup.removeFromParent();
    for (const axis of ["x", "y", "z"] as ClipAxis[]) {
      const ap = this.axes[axis];
      this._disposeHelper(ap);
      this.handles[axis].geometry.dispose();
      (this.handles[axis].material as THREE.Material).dispose();
    }
  }

  // ── Private: build geometry ─────────────────────────────────────────────────

  private _createAxisPlane(axis: ClipAxis): AxisPlane {
    const normal = new THREE.Vector3();
    normal[axis] = -1;
    const plane = new THREE.Plane(normal, 0);

    const helper = new THREE.Group();
    helper.visible = false;
    helper.renderOrder = 998;
    this.scene.add(helper);

    // Fill mesh (faint, also used as drag target)
    // polygonOffset pushes it back to avoid Z-fighting with frame and model
    const fillGeom = new THREE.PlaneGeometry(1, 1);
    const fillMat = new THREE.MeshBasicMaterial({
      color: AXIS_COLORS[axis],
      transparent: true,
      opacity: FILL_OPACITY,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: false,
      clippingPlanes: [],
    });
    const fill = new THREE.Mesh(fillGeom, fillMat);
    fill.renderOrder = 997;
    helper.add(fill);

    // Frame (LineLoop around the rectangle) — rendered on top of fill
    const hw = 0.5, hh = 0.5;
    const frameGeom = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-hw, -hh, 0),
      new THREE.Vector3(hw, -hh, 0),
      new THREE.Vector3(hw, hh, 0),
      new THREE.Vector3(-hw, hh, 0),
    ]);
    const frameMat = new THREE.LineBasicMaterial({
      color: AXIS_COLORS[axis],
      transparent: true,
      opacity: FRAME_OPACITY,
      depthTest: false,
      clippingPlanes: [],
    });
    const frame = new THREE.LineLoop(frameGeom, frameMat);
    frame.renderOrder = 999;
    helper.add(frame);

    // Orient perpendicular to axis
    if (axis === "x") helper.rotation.y = Math.PI / 2;
    if (axis === "y") helper.rotation.x = -Math.PI / 2;

    return { plane, helper, fill, enabled: false, offset: 0 };
  }

  /** Rebuild helper children to match current model size. */
  private _rebuildHelper(axis: ClipAxis, ap: AxisPlane): void {
    const s = this._planeSize(axis);

    // Update fill scale
    ap.fill.scale.set(s.x, s.y, 1);

    // Rebuild frame geometry with correct size
    const frame = ap.helper.children[1] as THREE.LineLoop;
    const hw = s.x / 2, hh = s.y / 2;
    frame.geometry.dispose();
    frame.geometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-hw, -hh, 0),
      new THREE.Vector3(hw, -hh, 0),
      new THREE.Vector3(hw, hh, 0),
      new THREE.Vector3(-hw, hh, 0),
    ]);
  }

  private _createHandle(axis: ClipAxis): THREE.Mesh {
    const geom = new THREE.CylinderGeometry(1, 1, 1, 8);
    const mat = new THREE.MeshBasicMaterial({
      color: AXIS_COLORS[axis],
      depthTest: false,
      clippingPlanes: [],
    });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.renderOrder = 1000;
    mesh.userData.clipAxis = axis;

    if (axis === "x") mesh.rotation.z = -Math.PI / 2;
    if (axis === "z") mesh.rotation.x = Math.PI / 2;

    this.gizmoGroup.add(mesh);
    return mesh;
  }

  private _planeSize(axis: ClipAxis): THREE.Vector2 {
    const s = this.modelSize;
    const f = PLANE_SIZE_FACTOR;
    switch (axis) {
      case "x": return new THREE.Vector2(s.z * f, s.y * f);
      case "y": return new THREE.Vector2(s.x * f, s.z * f);
      case "z": return new THREE.Vector2(s.x * f, s.y * f);
    }
  }

  private _updatePlanePosition(axis: ClipAxis): void {
    const ap = this.axes[axis];
    const pos = this.modelCenter.clone();
    pos[axis] += ap.offset;
    ap.helper.position.copy(pos);
    ap.plane.constant = pos[axis];
  }

  private _disposeHelper(ap: AxisPlane): void {
    for (const child of ap.helper.children) {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        (child.material as THREE.Material).dispose();
      } else if (child instanceof THREE.LineLoop) {
        child.geometry.dispose();
        (child.material as THREE.Material).dispose();
      }
    }
    ap.helper.removeFromParent();
  }

  // ── Private: interaction ────────────────────────────────────────────────────

  private _onPointerDown(e: PointerEvent): void {
    if (e.button !== 0) return;
    this._updatePointer(e);
    this.raycaster.setFromCamera(this.pointer, this.camera);

    // Check handle hits
    const handleMeshes = Object.values(this.handles);
    const hits = this.raycaster.intersectObjects(handleMeshes, false);
    if (hits.length > 0) {
      const axis = hits[0].object.userData.clipAxis as ClipAxis;
      const ap = this.axes[axis];
      if (!ap.enabled) {
        this.toggleAxis(axis);
      }
      this.dragging = axis;
      this.dragStartY = e.clientY;
      this.dragStartOffset = ap.offset;
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Check fill mesh hits (for dragging the plane)
    const fills = (["x", "y", "z"] as ClipAxis[])
      .filter((a) => this.axes[a].enabled && this.axes[a].helper.visible)
      .map((a) => this.axes[a].fill);
    const planeHits = this.raycaster.intersectObjects(fills, false);
    if (planeHits.length > 0) {
      const hit = planeHits[0].object;
      const axis = (["x", "y", "z"] as ClipAxis[]).find((a) => this.axes[a].fill === hit)!;
      this.dragging = axis;
      this.dragStartY = e.clientY;
      this.dragStartOffset = this.axes[axis].offset;
      e.preventDefault();
      e.stopPropagation();
    }
  }

  private _onPointerMove(e: PointerEvent): void {
    if (!this.dragging) {
      this._updatePointer(e);
      this.raycaster.setFromCamera(this.pointer, this.camera);
      const handleMeshes = Object.values(this.handles);
      const hits = this.raycaster.intersectObjects(handleMeshes, false);
      if (hits.length > 0) {
        this.container.style.cursor = "pointer";
        return;
      }
      const fills = (["x", "y", "z"] as ClipAxis[])
        .filter((a) => this.axes[a].enabled && this.axes[a].helper.visible)
        .map((a) => this.axes[a].fill);
      const planeHits = this.raycaster.intersectObjects(fills, false);
      this.container.style.cursor = planeHits.length > 0 ? "grab" : "";
      return;
    }

    const dy = e.clientY - this.dragStartY;
    const axis = this.dragging;
    const halfSize = this.modelSize[axis] * 0.5;
    const sensitivity = (this.modelSize[axis]) / this.container.clientHeight;
    const delta = -dy * sensitivity * 2;
    const newOffset = Math.max(-halfSize, Math.min(halfSize, this.dragStartOffset + delta));
    this.axes[axis].offset = newOffset;
    this._updatePlanePosition(axis);
    this.onChange?.();
    e.preventDefault();
    e.stopPropagation();
  }

  private _onPointerUp(_e: PointerEvent): void {
    if (this.dragging) {
      this.dragging = null;
      this.container.style.cursor = "";
    }
  }

  private _updatePointer(e: PointerEvent): void {
    const rect = this.container.getBoundingClientRect();
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  }
}
